import { toUserMessage } from '../../lib/errors.js';
import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonInput,
  IonItem,
  IonLabel,
  IonModal,
  IonTitle,
  IonToggle,
  IonToolbar,
  useIonToast,
} from '@ionic/react';
import { addCircleOutline, closeOutline } from 'ionicons/icons';
import { useEffect, useState } from 'react';
import { SearchPickerModal, PickerTriggerField } from '../SearchPickerModal.js';
import { useAuth } from '../../lib/auth/AuthProvider.js';
import { useActiveBusiness } from '../../lib/auth/useActiveBusiness.js';
import { useEnqueueMaster, useMasters } from '../../lib/data/queries.js';
import type { OperationalPersonnelRow } from '../../lib/auth/useOperationalIdentity.js';
import { FoodEditorModal } from './FoodEditorModal.js';
import { EquipmentEditorModal } from './EquipmentEditorModal.js';

export interface CleaningScheduleRecord {
  id: string;
  title: string;
  frequency: string;
  business_id: string;
  responsible_role: string | null;
  equipment_id: string | null;
  product_id: string | null;
  product_ids?: string[] | null;
  equipment_ids?: string[] | null;
  assigned_user_ids?: string[] | null;
  assigned_personnel_ids?: string[] | null;
  requires_photo?: boolean;
  version?: number;
}

export interface CleaningTaskEditorModalProps {
  isOpen: boolean;
  initial?: CleaningScheduleRecord;
  initialName?: string;
  onDismiss: () => void;
  onSaved?: (saved: CleaningScheduleRecord) => void;
  onRequestArchive?: (rec: { id: string; name: string; version: number }) => void;
}

interface CleaningProductRow {
  id: string;
  name: string;
  category?: string | null;
  archived_at?: string | null;
}

interface CleaningEquipmentRow {
  id: string;
  name: string;
}

interface InternalFormState {
  id: string;
  title: string;
  frequency: string;
  responsible_role: string;
  product_ids: string[];
  equipment_ids: string[];
  assigned_user_ids: string[];
  assigned_personnel_ids: string[];
  requires_photo: boolean;
  version: number;
}

function buildInitialState(initial: CleaningScheduleRecord | undefined, initialName: string | undefined): InternalFormState {
  if (initial) {
    return {
      id: initial.id,
      title: initial.title,
      frequency: initial.frequency,
      responsible_role: initial.responsible_role ?? '',
      product_ids: initial.product_ids ?? (initial.product_id ? [initial.product_id] : []),
      equipment_ids: initial.equipment_ids ?? (initial.equipment_id ? [initial.equipment_id] : []),
      assigned_user_ids: initial.assigned_user_ids ?? [],
      assigned_personnel_ids: initial.assigned_personnel_ids ?? [],
      requires_photo: initial.requires_photo ?? false,
      version: initial.version ?? 0,
    };
  }
  return {
    id: '',
    title: initialName ?? '',
    frequency: 'giornaliera',
    responsible_role: '',
    product_ids: [],
    equipment_ids: [],
    assigned_user_ids: [],
    assigned_personnel_ids: [],
    requires_photo: false,
    version: 0,
  };
}

export function CleaningTaskEditorModal(props: CleaningTaskEditorModalProps): JSX.Element {
  const { isOpen, initial, initialName, onDismiss, onSaved, onRequestArchive } = props;
  const { profile } = useAuth();
  void profile;
  const createMaster = useEnqueueMaster();
  const activeBusiness = useActiveBusiness();
  const { data: allFoods = [] } = useMasters<CleaningProductRow>('foods');
  const products = allFoods.filter((food) =>
    food.category === 'detersivi' && !food.archived_at
  );
  const { data: equipments = [] } = useMasters<CleaningEquipmentRow>('cleaning_equipments');
  const { data: personnel = [] } = useMasters<OperationalPersonnelRow>('tenant_personnel');

  const [form, setForm] = useState<InternalFormState>(() => buildInitialState(initial, initialName));
  const [presentToast] = useIonToast();
  const setToast = (msg: string) => {
    void presentToast({ message: msg, duration: 1800, position: 'top' });
  };
  // Sub-editor inline: l'utente può creare un prodotto o un'attrezzatura
  // direttamente da qui senza tornare alle rispettive pagine. Al salvataggio
  // il nuovo id viene aggiunto al rispettivo array selezionato.
  const [productEditorOpen, setProductEditorOpen] = useState(false);
  const [equipmentEditorOpen, setEquipmentEditorOpen] = useState(false);
  const [multiPicker, setMultiPicker] = useState<null | 'products' | 'equipments' | 'staff'>(null);

  useEffect(() => {
    if (isOpen) {
      setForm(buildInitialState(initial, initialName));
    }
  }, [isOpen, initial, initialName]);

  async function save(): Promise<void> {
    try {
      const title = form.title.trim();
      if (!title) {
        setToast('Titolo richiesto');
        return;
      }
      const ctx = await activeBusiness.resolve();
      if (!ctx) {
        setToast('Nessun locale attivo');
        return;
      }
      const id = form.id || crypto.randomUUID();
      const isUpdate = !!form.id;
      await createMaster.mutateAsync({
        table: 'cleaning_schedules',
        id,
        op: isUpdate ? 'update' : 'insert',
        base_version: form.version ?? 0,
        patch: {
          id,
          tenant_id: ctx.tenantId,
          business_id: ctx.businessId,
          title,
          frequency: form.frequency,
          responsible_role: form.responsible_role || null,
          requires_photo: form.requires_photo,
          equipment_id: form.equipment_ids[0] ?? null,
          product_id: form.product_ids[0] ?? null,
          product_ids: form.product_ids,
          equipment_ids: form.equipment_ids,
          assigned_user_ids: form.assigned_user_ids,
          assigned_personnel_ids: form.assigned_personnel_ids,
        },
      });
      setToast(isUpdate ? 'Attività aggiornata' : 'Attività creata');
      onSaved?.({
        id,
        title,
        frequency: form.frequency,
        business_id: ctx.businessId,
        responsible_role: form.responsible_role || null,
        equipment_id: form.equipment_ids[0] ?? null,
        product_id: form.product_ids[0] ?? null,
        product_ids: form.product_ids,
        equipment_ids: form.equipment_ids,
        assigned_user_ids: form.assigned_user_ids,
        assigned_personnel_ids: form.assigned_personnel_ids,
        requires_photo: form.requires_photo,
        version: (form.version ?? 0) + 1,
      });
      onDismiss();
    } catch (err: unknown) {
      setToast(toUserMessage(err));
    }
  }

  return (
    <>
      <IonModal data-testid="cleaning-task-editor" isOpen={isOpen} onDidDismiss={onDismiss}>
        <IonHeader>
          <IonToolbar>
            <IonTitle>{form.id ? 'Modifica attività' : 'Nuova attività di pulizia'}</IonTitle>
            <IonButtons slot="end">
              <IonButton onClick={onDismiss} aria-label="chiudi">
                <IonIcon icon={closeOutline} />
              </IonButton>
            </IonButtons>
          </IonToolbar>
        </IonHeader>
        <IonContent style={{ '--background': '#eef2f7' } as React.CSSProperties}>
          <div style={{ padding: '12px 16px 24px' }}>
            <h2 style={{ margin: '4px 4px 8px', fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              🧽 Attività
            </h2>
            <div style={{ background: '#fff', borderRadius: 14, padding: '4px 0', boxShadow: '0 1px 2px rgba(15,23,42,.06), 0 2px 6px rgba(15,23,42,.05)' }}>
              <IonItem lines="full" style={{ '--background': 'transparent' } as React.CSSProperties}>
                <IonLabel position="stacked">Titolo *</IonLabel>
                <IonInput
                  value={form.title}
                  placeholder="Pulizia piani di lavoro"
                  onIonInput={(ev) => setForm((f) => ({ ...f, title: String(ev.detail.value ?? '') }))}
                />
              </IonItem>
              <IonItem lines="none" style={{ '--background': 'transparent' } as React.CSSProperties}>
                <IonLabel position="stacked">Ruolo responsabile</IonLabel>
                <IonInput
                  value={form.responsible_role}
                  placeholder="staff / osa / ristoratore"
                  onIonInput={(ev) => setForm((f) => ({ ...f, responsible_role: String(ev.detail.value ?? '') }))}
                />
              </IonItem>
            </div>

            <h2 style={{ margin: '20px 4px 8px', fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              ⏰ Frequenza
            </h2>
            <div style={{ background: '#fff', borderRadius: 14, padding: '12px 14px', boxShadow: '0 1px 2px rgba(15,23,42,.06), 0 2px 6px rgba(15,23,42,.05)' }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {([
                  { v: 'giornaliera',  label: 'Giornaliera',  emoji: '☀️' },
                  { v: 'settimanale',  label: 'Settimanale',  emoji: '📅' },
                  { v: 'mensile',      label: 'Mensile',      emoji: '🗓️' },
                  { v: 'trimestrale',  label: 'Trimestrale',  emoji: '📆' },
                  { v: 'semestrale',   label: 'Semestrale',   emoji: '🍂' },
                  { v: 'annuale',      label: 'Annuale',      emoji: '🎄' },
                  { v: 'a_chiamata',   label: 'A chiamata',   emoji: '📞' },
                ] as const).map((freq) => {
                  const active = form.frequency === freq.v;
                  return (
                    <button
                      key={freq.v}
                      type="button"
                      onClick={() => setForm((f) => ({ ...f, frequency: freq.v }))}
                      style={{
                        border: `1px solid ${active ? '#0066cc' : '#cbd5e1'}`,
                        background: active ? '#0066cc' : '#fff',
                        color: active ? '#fff' : '#475569',
                        borderRadius: 999,
                        padding: '5px 11px',
                        fontSize: 12,
                        fontWeight: active ? 600 : 500,
                        cursor: 'pointer',
                        display: 'inline-flex', alignItems: 'center', gap: 4,
                      }}
                    >
                      <span style={{ fontSize: 13 }}>{freq.emoji}</span>
                      {freq.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <h2 style={{ margin: '20px 4px 8px', fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              🧴 Prodotti & 🔧 Attrezzature
            </h2>
            <div style={{ background: '#fff', borderRadius: 14, padding: '4px 0', boxShadow: '0 1px 2px rgba(15,23,42,.06), 0 2px 6px rgba(15,23,42,.05)' }}>
              <IonItem lines="full" style={{ '--background': 'transparent' } as React.CSSProperties}>
                <PickerTriggerField
                  label="Prodotti"
                  placeholder={products.length === 0 ? 'Nessun prodotto' : 'Tocca per selezionare'}
                  value={form.product_ids.length > 0
                    ? `${form.product_ids.length} selezionat${form.product_ids.length === 1 ? 'o' : 'i'}`
                    : undefined}
                  onClick={() => setMultiPicker('products')}
                />
              </IonItem>
              <IonItem button detail={false} onClick={() => setProductEditorOpen(true)} lines="full" style={{ '--background': 'transparent' } as React.CSSProperties}>
                <IonIcon icon={addCircleOutline} slot="start" color="primary" />
                <IonLabel color="primary" style={{ fontSize: 13 }}>Crea nuovo prodotto</IonLabel>
              </IonItem>
              <IonItem lines="full" style={{ '--background': 'transparent' } as React.CSSProperties}>
                <PickerTriggerField
                  label="Attrezzature"
                  placeholder={equipments.length === 0 ? 'Nessuna attrezzatura' : 'Tocca per selezionare'}
                  value={form.equipment_ids.length > 0
                    ? `${form.equipment_ids.length} selezionat${form.equipment_ids.length === 1 ? 'a' : 'e'}`
                    : undefined}
                  onClick={() => setMultiPicker('equipments')}
                />
              </IonItem>
              <IonItem button detail={false} onClick={() => setEquipmentEditorOpen(true)} lines="none" style={{ '--background': 'transparent' } as React.CSSProperties}>
                <IonIcon icon={addCircleOutline} slot="start" color="primary" />
                <IonLabel color="primary" style={{ fontSize: 13 }}>Crea nuova attrezzatura</IonLabel>
              </IonItem>
            </div>

            <h2 style={{ margin: '20px 4px 8px', fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              👥 Assegna a
            </h2>
            <div style={{ background: '#fff', borderRadius: 14, padding: '4px 0', boxShadow: '0 1px 2px rgba(15,23,42,.06), 0 2px 6px rgba(15,23,42,.05)' }}>
              <IonItem lines="none" style={{ '--background': 'transparent' } as React.CSSProperties}>
                <PickerTriggerField
                  label="Staff (multi)"
                  placeholder={personnel.length === 0 ? 'Nessuna persona nel MOD 05' : 'Tocca per assegnare'}
                  value={form.assigned_personnel_ids.length > 0
                    ? `${form.assigned_personnel_ids.length} assegnat${form.assigned_personnel_ids.length === 1 ? 'o' : 'i'}`
                    : undefined}
                  onClick={() => setMultiPicker('staff')}
                />
              </IonItem>
              <IonItem lines="none" style={{ '--background': 'transparent' } as React.CSSProperties}>
                <IonLabel>
                  <h3>Foto obbligatoria</h3>
                  <p>Richiede una foto della superficie o attrezzatura prima di registrare.</p>
                </IonLabel>
                <IonToggle
                  slot="end"
                  checked={form.requires_photo}
                  onIonChange={(event) => setForm((value) => ({ ...value, requires_photo: !!event.detail.checked }))}
                />
              </IonItem>
            </div>

            <div style={{ marginTop: 20, display: 'flex', gap: 8 }}>
              <IonButton
                expand="block"
                onClick={() => void save()}
                style={{ '--border-radius': '12px', flex: 2, height: 46 } as React.CSSProperties}
              >
                {form.id ? 'Salva modifiche' : 'Crea attività'}
              </IonButton>
              <IonButton
                expand="block"
                fill="outline"
                onClick={onDismiss}
                style={{ '--border-radius': '12px', flex: 1, height: 46 } as React.CSSProperties}
              >
                Annulla
              </IonButton>
            </div>
            {form.id && onRequestArchive && (
              <IonButton
                expand="block"
                color="danger"
                fill="clear"
                style={{ '--border-radius': '12px', marginTop: 8 } as React.CSSProperties}
                onClick={() => {
                  onRequestArchive({ id: form.id, name: form.title, version: form.version });
                  onDismiss();
                }}
              >
                Archivia attività
              </IonButton>
            )}
          </div>
        </IonContent>
      </IonModal>

      {/* Sub-editor inline: dopo creazione il nuovo id viene aggiunto al
          rispettivo array selezionato della task. */}
      <FoodEditorModal
        isOpen={productEditorOpen}
        detergentMode
        onDismiss={() => setProductEditorOpen(false)}
        onSaved={(p) => {
          if (p.id) {
            setForm((f) => ({ ...f, product_ids: [...f.product_ids, p.id] }));
            setToast(`"${p.name}" aggiunto ai prodotti`);
          }
        }}
      />
      <EquipmentEditorModal
        isOpen={equipmentEditorOpen}
        onDismiss={() => setEquipmentEditorOpen(false)}
        onSaved={(e) => {
          if (e.id) {
            setForm((f) => ({ ...f, equipment_ids: [...f.equipment_ids, e.id] }));
            setToast(`"${e.name}" aggiunta alle attrezzature`);
          }
        }}
      />

      {/* Selettori-ricerca multipli (prodotti / attrezzature / staff). */}
      <SearchPickerModal
        isOpen={multiPicker === 'products'}
        title="Prodotti"
        searchPlaceholder="Cerca prodotto…"
        multiple
        selectedIds={form.product_ids}
        emptyHint="Nessun prodotto. Creane uno con il pulsante qui sopra."
        primaryAction={{ label: 'Crea nuovo prodotto', onClick: () => { setMultiPicker(null); setProductEditorOpen(true); } }}
        items={products.slice().sort((a, b) => a.name.localeCompare(b.name)).map((p) => ({ id: p.id, label: p.name }))}
        onConfirm={(ids) => setForm((f) => ({ ...f, product_ids: ids }))}
        onClose={() => setMultiPicker(null)}
      />
      <SearchPickerModal
        isOpen={multiPicker === 'equipments'}
        title="Attrezzature"
        searchPlaceholder="Cerca attrezzatura…"
        multiple
        selectedIds={form.equipment_ids}
        emptyHint="Nessuna attrezzatura. Creane una con il pulsante qui sopra."
        primaryAction={{ label: 'Crea nuova attrezzatura', onClick: () => { setMultiPicker(null); setEquipmentEditorOpen(true); } }}
        items={equipments.slice().sort((a, b) => a.name.localeCompare(b.name)).map((e) => ({ id: e.id, label: e.name }))}
        onConfirm={(ids) => setForm((f) => ({ ...f, equipment_ids: ids }))}
        onClose={() => setMultiPicker(null)}
      />
      <SearchPickerModal
        isOpen={multiPicker === 'staff'}
        title="Assegna staff"
        searchPlaceholder="Cerca persona…"
        multiple
        selectedIds={form.assigned_personnel_ids}
        emptyHint="Nessuna persona attiva nel MOD 05."
        items={personnel.filter((person) => person.attivo !== false && !person.archived_at).map((person) => ({ id: person.id, label: person.full_name, sublabel: person.role }))}
        onConfirm={(ids) => setForm((f) => ({ ...f, assigned_personnel_ids: ids }))}
        onClose={() => setMultiPicker(null)}
      />
    </>
  );
}
